/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import {
	IOperationsServiceRequest,
	IServices,
	OperationalRequestCategoryEnum,
	OperationsRequestTypeEnum,
} from 'features/salesOrder/types';
import { ServiceSelector } from './ServiceSelector';
import { FileUploader } from './FileUploader';
import { createOperationalRequest } from 'features/salesOrder/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { fileToBase64Attachment } from '../../helpers';
import { OPERATIONS_REQUEST_TYPE_LABELS } from '../../../../../features/salesOrder/constants';

interface OperationsServiceRequestModalProps {
	open: boolean;
	onClose: () => void;
	services: IServices[];
	saleOrderId: string;
	onSuccess: (caseId: number) => void;
	onSetAlert: (alert: ShowAlertState) => void;
}

const operationsRequestTypes: {
	value: OperationsRequestTypeEnum;
	label: string;
}[] = (Object.entries(OPERATIONS_REQUEST_TYPE_LABELS) as Array<[OperationsRequestTypeEnum, string]>).map(
	([value, label]) => ({
		value,
		label,
	}),
);

export const OperationsServiceRequestModal: React.FC<OperationsServiceRequestModalProps> = ({
	open,
	onClose,
	services,
	saleOrderId,
	onSuccess,
	onSetAlert,
}) => {
	const [requestType, setRequestType] = useState<OperationsRequestTypeEnum>(OperationsRequestTypeEnum.LateArrival);
	const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
	const [observations, setObservations] = useState('');
	const [attachments, setAttachments] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);

	const handleRequestTypeChange = (event: SelectChangeEvent) => {
		setRequestType(event.target.value as OperationsRequestTypeEnum);
	};

	const handleSubmit = async () => {
		if (selectedServiceIds.length === 0) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Debe seleccionar al menos un servicio',
			});
			return;
		}

		try {
			setLoading(true);

			const files = Array.from(attachments.length > 0 ? attachments : []);

			const filesToAttachments = await Promise.all(files.map(fileToBase64Attachment));

			const requestData: IOperationsServiceRequest = {
				requestCategory: OperationalRequestCategoryEnum.Operations,
				requestType,
				saleOrderId,
				serviceIds: selectedServiceIds,
				observations: observations || undefined,
				attachments: filesToAttachments.length > 0 ? filesToAttachments : undefined,
			};

			const response = await createOperationalRequest(requestData);
			onSuccess(response.data.caseId);
			onSetAlert({
				show: true,
				severity: 'success',
				message: `Solicitud creada exitosamente. Caso #${response.data.caseId} creado en el CRM.`,
			});

			// Reset form
			setSelectedServiceIds([]);
			setObservations('');
			setAttachments([]);
			onClose();
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la solicitud'),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setSelectedServiceIds([]);
			setObservations('');
			setAttachments([]);
			onClose();
		}
	};

	// Filtrar servicios: excluir Flight y FlightAncillary
	const filterServices = (service: IServices) => {
		return service.serviceType !== 'Flight' && service.serviceType !== 'FlightAncillary';
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Solicitud al equipo de operaciones de servicios</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
					<FormControl fullWidth>
						<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
							Tipo de solicitud *
						</Typography>
						<Select value={requestType} onChange={handleRequestTypeChange} disabled={loading}>
							{operationsRequestTypes.map((type) => (
								<MenuItem key={type.value} value={type.value}>
									{type.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<ServiceSelector
						services={services}
						selectedServiceIds={selectedServiceIds}
						onChange={setSelectedServiceIds}
						filterByType={filterServices}
						disabled={loading}
					/>

					<TextField
						label="Observaciones o notas para la solicitud"
						multiline
						rows={4}
						value={observations}
						onChange={(e) => setObservations(e.target.value)}
						disabled={loading}
						fullWidth
					/>

					<FileUploader
						files={attachments}
						onChange={setAttachments}
						accept=".pdf,.jpg,.jpeg,.png"
						maxFiles={10}
						maxSizeMB={10}
						disabled={loading}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancelar
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={loading}
					startIcon={loading && <CircularProgress size={16} />}
				>
					Crear solicitud
				</Button>
			</DialogActions>
		</Dialog>
	);
};
